import * as yup from 'yup';
import translation from 'i18n/translation.json';
import { GARegisterEvent, getBankCodeByAccountNumber, getFixedLengthError } from 'utils';
import type { UserInfo } from 'interfaces';
import type { IBanks } from './useFetchBanks';

type TrackFieldError = (fieldName: string, errorText: string) => void;

const createFieldErrorTracker = (data?: UserInfo | null, type?: string): TrackFieldError => {
  const registration = data?.registration_type;
  const stage = type !== 'change' ? 'issue_method' : 'change_issue_method';

  return (fieldName, errorText) => {
    GARegisterEvent({
      event: 'error_data',
      stage,
      field_name: fieldName,
      error_text: errorText,
      UID: data?.user_uuid,
      ...(type !== 'change' && { label: 'sign_up', registration }),
    });
  };
};

const createBankAccountTypeSchema = (trackFieldError: TrackFieldError) =>
  yup.string().required(() => {
    trackFieldError('bank_account_type', 'error_frontend: Bank account type is required');
    return translation.errors.requiredField;
  });

const createBankAccountNumberSchema = (
  trackFieldError: TrackFieldError,
  isBankPrefixValid: (value: string) => boolean,
  bankMismatchErrorText: string,
) =>
  yup
    .string()
    .trim()
    .required(() => {
      trackFieldError('bank_account_number', 'error_frontend: Bank account number is required');
      return translation.errors.requiredField;
    })
    .matches(/^\d{3}-\d{3}-\d{12}-\d{2}$/, ({ value }) => {
      if (value) {
        trackFieldError('bank_account_number', 'error_frontend: Bank account number should contain 20 digits');
        return getFixedLengthError(translation.errors.lengthFixed, 20);
      }
      return true;
    })
    .test('is-bank-code-matches-account', translation.errors.invalidBankAccountNumber, value => {
      if (value && !isBankPrefixValid(value)) {
        trackFieldError('bank_account_number', bankMismatchErrorText);
        return false;
      }

      return true;
    });

export const usePaymentInfoSchema = (bankCode: string | null, data?: UserInfo | null, type?: string) => {
  const trackFieldError = createFieldErrorTracker(data, type);

  // NOTE: to disable ?? eslint rule, bankCode can be ''
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const bankCodeValue = bankCode || data?.bank_account?.bank_code;

  const schema = yup.object().shape(
    {
      bank_code: yup.string().required(() => {
        trackFieldError('bank_code', 'error_frontend: Bank code is required');
        return translation.errors.requiredField;
      }),
      bank_account_type: createBankAccountTypeSchema(trackFieldError),
      bank_account_number: createBankAccountNumberSchema(
        trackFieldError,
        value => value.substring(0, 3) === bankCodeValue,
        'error_frontend: Bank account number doesnt relate to bank',
      ),
    },
    [],
  );

  const defaultValue: yup.InferType<typeof schema> = {
    bank_code: data?.bank_account?.bank_code ?? '',
    bank_account_type: data?.bank_account?.bank_account_type ?? '',
    bank_account_number: data?.bank_account?.bank_account_number ?? '',
  };

  return { schema, defaultValue };
};

export const useBankAccountNumberSchema = (banks: IBanks[], data?: UserInfo | null, type?: string) => {
  const trackFieldError = createFieldErrorTracker(data, type);

  const schema = yup.object().shape({
    bank_account_type: createBankAccountTypeSchema(trackFieldError),
    bank_account_number: createBankAccountNumberSchema(
      trackFieldError,
      value => Boolean(getBankCodeByAccountNumber(banks, value)),
      'error_frontend: Bank account number doesnt relate to any bank',
    ),
  });

  const defaultValue: yup.InferType<typeof schema> = {
    bank_account_type: data?.bank_account?.bank_account_type ?? '',
    bank_account_number: data?.bank_account?.bank_account_number ?? '',
  };

  return { schema, defaultValue };
};
